import {
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
} from "@tanstack/react-router";

import { useAuth } from "@/lib/auth-context";
import { VaultFilesContainer } from "@/features/vault/containers/vault-files-container";
import { VaultHomeContainer } from "@/features/vault/containers/vault-home-container";
import { VaultTrusteesContainer } from "@/features/vault/containers/vault-trustees-container";
import { TrusteeDashboardContainer } from "@/features/trustee/containers/trustee-dashboard-container";
import { TrusteeUnlockContainer } from "@/features/trustee/containers/trustee-unlock-container";
import { TrusteeClaimContainer } from "@/features/trustee/containers/trustee-claim-container";
import { ProfileContainer } from "@/features/settings/profile-container";
import { PreferencesContainer } from "@/features/settings/preferences-container";
import { LoginRoute } from "@/routes/login";
import { RootLayout } from "@/routes/root";
import { RequireAuth, RequireCapability } from "@/routes/app-shell";
import { SettingsLayout } from "@/routes/settings-layout";
import { VerifyRoute } from "@/routes/verify";

/**
 * Code-based route tree (no file-based codegen) so the scaffold is fully
 * self-contained. Authenticated screens are wrapped in <RequireAuth>, which
 * renders the app chrome and gates on the vault Firebase session (component-level
 * gating rather than the advisor app's claim-reading `beforeLoad` guards).
 */
const rootRoute = createRootRoute({ component: RootLayout });

function IndexGate() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="bg-background flex min-h-screen items-center justify-center">
        <p className="text-muted-foreground">Loading…</p>
      </div>
    );
  }
  if (!user) return <LoginRoute />;
  return (
    <RequireAuth>
      <VaultHomeContainer />
    </RequireAuth>
  );
}

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: IndexGate,
});

const filesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/files",
  component: () => (
    <RequireAuth>
      <RequireCapability need="owner">
        <VaultFilesContainer />
      </RequireCapability>
    </RequireAuth>
  ),
});

const trusteesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/trustees",
  component: () => (
    <RequireAuth>
      <RequireCapability need="owner">
        <VaultTrusteesContainer />
      </RequireCapability>
    </RequireAuth>
  ),
});

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings",
  component: () => (
    <RequireAuth>
      <SettingsLayout />
    </RequireAuth>
  ),
});

const settingsIndexRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/settings/profile" });
  },
});

const profileRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "profile",
  component: ProfileContainer,
});

const preferencesRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "preferences",
  component: PreferencesContainer,
});

const verifyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/verify",
  component: VerifyRoute,
});

// ── Trustee / heir routes ─────────────────────────────────────────────────────
// Heirs live in the vault app (secondary Firebase project). `/trustee/claim` is
// the magic-link landing and must be reachable pre-auth (it signs the trustee in);
// the dashboard and unlock portal sit behind RequireAuth.

const trusteeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/trustee",
  component: () => (
    <RequireAuth>
      <RequireCapability need="heir">
        <TrusteeDashboardContainer />
      </RequireCapability>
    </RequireAuth>
  ),
});

const trusteeUnlockRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/trustee/unlock",
  component: () => (
    <RequireAuth>
      <RequireCapability need="heir">
        <TrusteeUnlockContainer />
      </RequireCapability>
    </RequireAuth>
  ),
});

const trusteeClaimRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/trustee/claim",
  component: TrusteeClaimContainer,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  filesRoute,
  trusteesRoute,
  settingsRoute.addChildren([
    settingsIndexRoute,
    profileRoute,
    preferencesRoute,
  ]),
  verifyRoute,
  trusteeRoute,
  trusteeUnlockRoute,
  trusteeClaimRoute,
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
